/**
 * Central product copy — Phase 17 copy map.
 * @see docs/23_Guided_Learning_Vision.md §5.1
 */
export const copy = {
  brand: {
    name: 'Lexio',
    tagline: 'Understand English as you read',
    beta: 'Beta',
  },
  nav: {
    today: 'Today',
    catalog: 'Browse catalog',
    words: 'Your vocabulary',
    reading: 'Reading',
    refresh: 'Refresh your memory',
    challenge: 'Challenge yourself',
    progress: 'Progress',
    practice: 'Practice',
    settings: 'Settings',
    faq: 'FAQ',
    /** Short labels for bottom nav only — page titles use full names above */
    mobileWords: 'Vocab',
    mobileReview: 'Review',
    more: 'More',
  },
  pages: {
    today: 'Today',
    catalog: 'Browse catalog',
    words: 'Your vocabulary',
    reading: 'Reading',
    refresh: 'Refresh your memory',
    challenge: 'Challenge yourself',
    progress: 'Progress',
    practice: 'Practice',
    settings: 'Settings',
    continueLearning: 'Continue Learning',
    keepLearning: 'Keep Learning',
  },
  actions: {
    explainSimply: 'Explain simply',
    explainInNative: 'In my language',
    simpler: 'Simpler',
    markRead: 'Finished reading',
    continueSession: 'Continue session',
    alreadyReadHint: 'You already read this article. Continue your session.',
    saveWord: 'Save this word',
    startRefresh: 'Start refreshing',
    readyToRefresh: (count: number) =>
      count === 1 ? 'Ready to refresh 1 word' : `Ready to refresh ${count} words`,
    browseReading: 'Find something to read',
    startReading: 'Start reading',
    addWords: 'Add words',
    startPractice: 'Start practice',
    cancel: 'Cancel',
    logout: 'Log out',
    generateQuestions: 'Reflect on what you read',
    generateQuestionsHint: 'A few short questions to help the article stick — one at a time, no pressure.',
    shareAnswer: 'Share my answer',
    thinkingWithYou: 'Thinking with you…',
    nextQuestion: 'Next question',
    seeSummary: 'See your reading summary',
    tryAgainAnswers: 'Go through again',
  },
  comprehension: {
    title: 'Reflect on what you read',
    confidenceLabel: 'How confident are you?',
    aboutYourAnswer: 'About your answer',
    aboutTheArticle: 'In this article',
    whyThisWord: 'Why this word?',
    takeAnotherLook: 'Take another look',
    tryAgain: 'Try again',
    showExplanation: 'Show explanation',
    skipForNow: 'Skip for now',
    skippedHeading: 'Saved for later',
    skippedNote: 'You can come back to this question anytime — no pressure.',
    skippedInSummary: (skipped: number, total: number) =>
      skipped === total
        ? `You skipped all ${total} questions for now — that's fine. You can reflect again whenever you're ready.`
        : `You skipped ${skipped} of ${total} questions for now — you can always go through them again.`,
    fromArticle: 'From the article:',
    readingSummary: 'Reading summary',
    nextStep: 'Your next step',
    readyAnother: 'Ready for another article?',
    greatClose: 'Great! You engaged deeply with this article.',
    warmClose: 'Nice work reflecting on what you read.',
    wordsToRevisit: 'Words worth another look',
    understoodWell: 'What you understood well',
    questionOf: (current: number, total: number) => `Question ${current} of ${total}`,
    answerPlaceholder: 'Share what you remember or think…',
    answerTooLong: (max: number) => `Keep your answer under ${max} characters.`,
    answerInvalid: 'Your answer contains characters we cannot accept. Please rephrase it.',
    charactersLeft: (left: number) => `${left} characters left`,
    confidence: {
      guess: 'Guess',
      not_sure: 'Not sure',
      pretty_sure: 'Pretty sure',
      absolutely_sure: 'Absolutely sure',
    },
    types: {
      recall: 'Recall',
      inference: 'Inference',
      vocabulary: 'Vocabulary',
      personal: 'About you',
      factual: 'Recall',
    },
  },
  coach: {
    title: 'A note from your coach',
    dismiss: 'Dismiss',
  },
  todayPlan: {
    heading: 'Your day',
    refresh: 'Memory refresh',
    newWords: 'New words saved',
    reading: 'Reading',
    practice: 'AI practice',
    allCaughtUp: 'All refreshed for now',
  },
  ai: {
    explanationHeading: 'Simple explanation',
    practiceSelectWords: 'Pick vocabulary to practice',
    practiceWordLimit: (selected: number, max: number) => `${selected} / ${max} items`,
    practiceWordLimitHint: (max: number) =>
      `Up to ${max} items per story or lesson (based on your level)`,
    practiceWordLimitReached: (max: number) => `Maximum ${max} items — deselect one to add another`,
    practiceSelectRandom: (count: number) => `Pick ${count} random`,
    creditsHeading: 'Daily AI credits',
    creditsShort: 'AI left today',
    creditsOfLimit: (used: number, limit: number) => `${used} used · ${limit} daily limit`,
    creditsCompact: (remaining: number, limit: number) =>
      `${remaining} of ${limit} AI credits left today`,
    creditsHint:
      'Covers article generation, practice stories, and comprehension questions. Word explanations from cache are free.',
    creditsCostPractice: (cost: string) => `Story or lesson: ${cost}`,
    creditsCostArticle: (cost: string) => `Personal article: ${cost}`,
    creditsCostComprehension: (cost: string) => `Reading reflection questions: ${cost}`,
    creditsCostComprehensionCheck: (cost: string) => `Coach feedback per answer: ${cost}`,
    creditsReset: (time: string) => `Resets at ${time} (UTC). Changing timezone does not reset credits.`,
    creditsExceeded: 'Daily AI credits used up. They reset at midnight UTC.',
    phraseLimitExceeded:
      'Daily phrase explanation limit reached. Already explained phrases still work — try again tomorrow.',
    phraseQuotaCompact: (remaining: number, limit: number) =>
      `${remaining} of ${limit} new phrase explanations left today`,
  },
  achievements: {
    heading: 'Your wins',
    subheading: 'Meaningful milestones from your learning journey.',
    unlocked: 'Achievement unlocked',
  },
  onboarding: {
    heroTitle: 'Start with an easy read',
    heroDescription:
      'Open a beginner article and tap words you want to learn — they go straight into your vocabulary.',
    dashboardPrompt: 'Your dashboard will fill in as you read, save words, and review.',
    dashboardHint: 'Not enough data yet — here is what your day will look like.',
    vocabEmpty: 'Words you save while reading will show up here.',
    vocabPreview: 'Your vocabulary breakdown will appear here once you save your first word.',
    todayPlan: 'Read one article and save words as you go.',
    todayPlanPreview: 'Daily goals appear here once you start learning.',
    wizardTitle: 'Welcome to Lexio',
    wizardSubtitle: 'Tell us a bit about yourself so we can personalize your learning.',
    stepNativeTitle: 'What is your native language?',
    stepNativeDescription: 'We use it for translations, word popups, and AI explanations.',
    stepLevelTitle: 'What is your English level?',
    stepLevelDescription: 'Articles, reviews, and practice will match this level.',
    stepDurationTitle: 'How long do you want to study each day?',
    stepDurationDescription: 'Your daily guided session will fit this time.',
    stepDurationLabel: (minutes: number) => `${minutes} minutes`,
    stepTopicsTitle: 'What do you like to read about?',
    stepTopicsDescription: 'Pick a few topics — we use them to recommend articles. You can skip this step.',
    stepTopicsSkip: 'Skip for now',
    stepCompleteTitle: "You're all set!",
    stepCompleteDescription:
      'Lexio will tailor reading, vocabulary, and daily sessions to your choices.',
    stepCompleteHint: 'You can change any of these anytime in Settings.',
    next: 'Continue',
    back: 'Back',
    finish: 'Start learning',
  },
  learningDemo: {
    skip: 'Skip intro',
    introTitle: 'One day of learning',
    introPhilosophy:
      "You don't need to memorize thousands of words. Just read, save the words you care about, and Lexio will help you remember them at the right time.",
    introCta: 'Show me how',
    questStep: (current: number, total: number) => `Step ${current} of ${total}`,
    readTitle: 'Read',
    readBody: 'Read the short passage. When you meet an unfamiliar word, tap it.',
    understandTitle: 'Understand',
    understandBody: 'We explain the word in context. If it helps you, save it to your vocabulary.',
    vocabularyTitle: 'Your vocabulary',
    vocabularyBody: 'Your saved words live here — this is your personal list to learn.',
    refreshTitle: 'Refresh',
    refreshBody:
      'Later today or tomorrow, come back to review. Spaced repetition helps words stick.',
    todayTitle: 'Today',
    todayBody:
      'Your home base for the day — we prepare what to read and review based on your progress.',
    tapWordHint: 'Tap the highlighted word',
    saveWord: 'Save word',
    saving: 'Saving…',
    saved: 'Saved!',
    savedWordLabel: 'Your first word',
    vocabularyStatusNew: 'New',
    continue: 'Continue',
    completeTitle: "You're ready",
    completeBody:
      "You've already started your vocabulary. Open your first article and tap any word you don't know.",
    completeCta: 'Start reading',
    replay: 'Replay intro',
    replayDescription: 'Walk through the interactive intro again.',
    readLabel: 'Read',
    understandLabel: 'Understand',
    means: 'Means',
    inContext: 'In this sentence',
    definitionLabel: 'Definition',
    exampleLabel: 'Example',
    vocabularyMoreHint:
      'Your vocabulary also tracks progress, review history, and personal notes — more than a simple word list.',
    refreshFlipHint: 'Tap the card to flip',
    refreshCardLabel: 'Review card',
    refreshMoreHint:
      'Refresh also has several review modes, session stats, and spaced-repetition scheduling — this is just a quick peek.',
    todayPreviewHint: 'A typical day looks like this:',
    todayCardLabel: 'Your day',
    todayStepReading: 'Continue reading',
    todayStepReadingHint: 'Pick up an article at your level',
    todayStepNewWords: 'Save new words',
    todayStepNewWordsHint: 'Tap words while reading to grow your list',
    todayStepRefresh: 'Refresh memory',
    todayStepRefreshHint: 'Review due words so they stick',
    todayMoreHint: 'Today brings reading, vocabulary, and review together in one place.',
    completeHelpHint:
      'Questions? Open FAQ from the sidebar (or More on mobile). To share an idea or report a bug, tap Feedback — it sits right above FAQ.',
  },
  review: {
    modeClassic: 'Word → translation',
    modeReverse: 'Translation → word',
    modeFillBlank: 'Fill in blank',
    modeMultipleChoice: 'Multiple choice',
    checkAnswer: 'Check answer',
    fillBlankNeedAnswer: 'Type your answer first',
    fillBlankNeedCheck: 'Tap Check answer before rating',
    fillBlankCorrect: 'Correct!',
    fillBlankSoftCorrect: 'Counted — looks like a typo. Canonical:',
    fillBlankWrong: 'Wrong — correct answer:',
    multipleChoiceNeedOption: 'Select an option first',
    /** C4 — grammar Episode card eyebrow (not “Prompt”) */
    meetAgain: 'Meet again',
    /** C4 — back of grammar Episode (hero_l1), not textbook tense */
    momentMeaning: 'Meaning',
  },
  exercise: {
    checkAnswer: 'Check answer',
    fillBlankSelectOption: 'Choose an answer to continue',
    multipleChoiceSelectOption: 'Choose an option to continue',
    fillBlankTypeAnswer: 'Type your answer, then tap Check answer',
    readyToContinue: 'Ready to continue',
    goToQuestions: 'Continue to questions',
    backToText: 'Back to text',
    readScenario: 'Read the scenario',
  },
  reading: {
    pageDescription: 'Improve your vocabulary by reading articles at your level.',
    hubEyebrow: 'Your reading',
    hubTitle: 'Start with a pick or create your own',
    pickedForYou: 'Picked for you',
    tabForYou: 'For you',
    tabLibrary: 'Library',
    libraryHeading: 'Library',
    noRecommendationTitle: 'No recommendation yet',
    noRecommendationHint: 'Open the library tab or generate a personal article from your words.',
    filterAll: 'All',
    filterUnread: 'Unread',
    filterRead: 'Read',
    createArticle: 'New article',
    generatePersonalizedTitle: 'Article for your vocabulary',
    generatePersonalizedHint: (max: number) =>
      `AI weaves up to ${max} items from your vocabulary into a short personal read.`,
    generatePersonalizedPrivate:
      'Only you can see this article — it is not shared with other learners.',
    generatePersonalizedWordCount: (count: number, max: number) =>
      count < max
        ? `${count} from your vocabulary`
        : `Up to ${max} from your vocabulary`,
    shuffleWords: 'Shuffle',
    generatePersonalizedNoWords: 'Save words or phrases while reading to unlock personal articles.',
    generatePersonalizedCta: 'Generate article for my vocabulary',
    generatePersonalizedLoading: 'Writing your article…',
    generatePersonalizedFailed: 'Could not generate an article. Please try again.',
    generatePersonalizedFieldLevel: 'Level',
    generatePersonalizedFieldDuration: 'Reading time',
    generatePersonalizedFieldTopics: 'Topics',
    generatePersonalizedTopicsHint:
      'Choose up to 2 topics. Leave empty to pick from your settings preferences.',
    generatePersonalizedDurationMinutes: (minutes: number) => `${minutes} min`,
    articleGeneratedTitle: 'Article saved to your Library',
    articleGeneratedBody:
      'Your personal article is ready. You can always find it again in the Library tab.',
    articleGeneratedStart: 'Start reading',
    articleGeneratedLibrary: 'Go to Library',
    backToLibrary: 'Back to library',
  },
  recommendation: {
    whyArticle: 'Why this article?',
    dueWords: 'Due words',
    difficultyComfortable: 'Comfortable',
    difficultyStretch: 'Stretch',
    difficultyChallenging: 'Challenging',
  },
  admin: {
    navLabel: 'Admin',
    hubTitle: 'Admin',
    hubDescription: 'Platform management — articles, feedback, feature flags, and more.',
    hubHint: 'Admin-only tools. New sections appear here as the platform grows.',
    backToAdmin: 'Back to admin',
    createArticleTitle: 'Create article',
    createArticleDescription: 'Publish a new reading article for the library. It will be indexed for word popups automatically.',
    createArticleCardDescription: 'Publish reading articles and generate drafts with AI.',
    createArticleSubmit: 'Publish article',
    createArticleFailed: 'Failed to create article. Please try again.',
    createArticleValidation: 'Title and content are required.',
    backToReading: 'Back to reading',
    fieldTitle: 'Title',
    fieldContent: 'Content',
    fieldCefr: 'CEFR level',
    fieldEstimatedTime: 'Reading time',
    fieldAuthor: 'Author',
    fieldTags: 'Tags',
    fieldTagsHint: 'Choose up to 2 topics for the library article.',
    fieldImageUrl: 'Cover image URL',
    fieldSourceUrl: 'Source URL',
    fieldPublished: 'Publish immediately',
    fieldTopic: 'Topic (optional)',
    fieldTopicHint: 'Leave empty — AI picks a fresh angle. Fill only to steer a specific theme.',
    suggestFromInventory: 'Suggest from inventory',
    suggestFromInventoryHint: 'Fills level, reading time, and underused tags from the scarcest shared-library gap.',
    suggestFromInventoryFailed: 'Could not load inventory suggestions.',
    durationMinutes: (minutes: number) => `${minutes} min`,
    generateArticle: 'Generate with AI',
    generateArticleHint: 'Fill in level, time, and tags, then generate title and text.',
    generateArticleFailed: 'Failed to generate article. Check your OpenAI API key and try again.',
    flagsTitle: 'Feature flags',
    flagsDescription: 'Toggle features and control gradual rollout. Admins always receive enabled flags regardless of rollout percentage.',
    flagsCardDescription: 'Gradual rollout and kill switches for platform features.',
    flagsLoading: 'Loading feature flags…',
    flagsLoadFailed: 'Failed to load feature flags.',
    flagsUpdateFailed: 'Failed to update feature flag.',
    flagsGroupIntelligence: 'Intelligence',
    flagsGroupContent: 'Content & Trust',
    flagsGroupMonetization: 'Monetization',
    flagsGroupOther: 'Other flags',
    flagsUsedBy: 'Used by',
    flagsBackend: 'Backend',
    flagsFrontend: 'Frontend',
    flagsWhenEnabled: 'When enabled',
    flagsWhenDisabled: 'When disabled',
    flagsRolloutBehavior: 'Rollout behavior',
    flagsDefaultState: 'Default state',
    flagsNotes: 'Notes',
    flagsHowItWorksTitle: 'How feature flags work',
    flagsHowItWorks: [
      'Each flag has a master switch (enabled/disabled) and a rollout slider (0–100%).',
      'If the master switch is OFF, the feature is unavailable to everyone.',
      'If ON, non-admin users are included based on a stable hash of their user ID and flag key — the same user always gets the same result for a given percentage.',
      'Admins always receive enabled flags when the master switch is ON, regardless of rollout %.',
      'Flag config is cached in Redis for 60 seconds after changes.',
      'Checks happen server-side only; the frontend has no feature-flag logic.',
    ],
    metricsTitle: 'Product metrics',
    metricsDescription: 'North Star and supporting metrics — is Lexio actually working?',
    metricsCardDescription: 'Retention, engagement pulse, completion rates, and unit economics.',
    metricsLoadFailed: 'Could not load metrics.',
    metricsPeriodLabel: 'Time range',
    metricsPeriodHint: 'North Star, retention, and catalog counts are fixed — engagement, completion, and economics follow the selected range.',
    metricsSections: {
      north_star: 'North Star',
      engagement: 'Today',
      retention: 'Retention',
      completion: 'Completion',
      economics: 'Unit Economics',
      catalog: 'Language Database',
    },
    usersTitle: 'Users',
    usersDescription: 'Promote users to admin or grant premium access.',
    usersCardDescription: 'Search users, change roles, and grant premium.',
    usersLoading: 'Loading users…',
    usersLoadFailed: 'Failed to load users.',
    usersUpdateFailed: 'Failed to update user.',
    usersSearchPlaceholder: 'Search by email or name…',
    usersSearch: 'Search',
    usersEmpty: 'No users found.',
    usersPrev: 'Previous',
    usersNext: 'Next',
    usersJoined: 'Joined',
    usersMakeAdmin: 'Make admin',
    usersDemoteAdmin: 'Remove admin',
    usersGrantPremium: 'Grant premium',
    usersRevokePremium: 'Revoke premium',
    usersPremiumGranted: 'Premium granted',
    usersPromoteConfirm: 'Promote {email} to admin? They will get full admin access.',
    usersDemoteConfirm: 'Remove admin role from {email}?',
    usersGrantPremiumConfirm: 'Grant premium to {email}? They will get PRO-tier AI limits.',
    usersRevokePremiumConfirm: 'Revoke premium from {email}? They will return to normal rollout rules.',
    usersHowItWorksTitle: 'How user management works',
    usersHowItWorks: [
      'Admins can promote any user to admin — they get access to this panel and unlimited AI quotas.',
      'You cannot demote yourself or remove the last remaining admin.',
      'Premium grants PRO-tier limits to a specific user, independent of the global premium rollout flag.',
      'Admins already have unlimited access — premium toggle is hidden for admin accounts.',
      'Role changes take effect immediately on the server; the affected user may need to refresh to see admin UI.',
    ],
    feedbackTitle: 'Feedback',
    feedbackDescription: 'Messages from Settings and FAQ. Filter like mail — unread first.',
    feedbackCardDescription: 'Read user feedback without digging in the database.',
    feedbackLoading: 'Loading feedback…',
    feedbackLoadFailed: 'Failed to load feedback.',
    feedbackEmpty: 'No feedback in this folder.',
    feedbackAnonymous: 'Anonymous',
    feedbackFilterAll: 'All',
    feedbackFilterUnread: 'Unread',
    feedbackFilterRead: 'Read',
    feedbackMarkRead: 'Mark as read',
    feedbackMarkUnread: 'Mark as unread',
    feedbackReadBy: 'Read by {email}',
    feedbackUpdateFailed: 'Could not update feedback.',
  },
  vocabulary: {
    addWordTitle: 'Add word or phrase',
    addWordSubmit: 'Add',
    addItem: 'Add',
    searchPlaceholder: 'Search vocabulary…',
    allItems: 'All items',
    collectionFilterAria: 'Filter by collection',
    itemCount: (total: number) => (total === 1 ? '1 item' : `${total} items`),
    wordLabel: 'English word or phrase',
    wordPlaceholder: 'e.g. garden or give up',
    wordHint: 'Enter a word or a multi-word phrase. We will look up the translation in your language.',
    invalidWordFormat: 'Enter a valid English word — letters only, 2–50 characters.',
    invalidPhraseFormat: 'Enter a valid English phrase — letters and spaces only, 3–200 characters.',
    phraseNotInCatalog:
      'This phrase is not in our catalog yet. Browse Catalog to find phrases and idioms, or add one while reading.',
    phraseAlreadyExists: 'This phrase is already in your vocabulary.',
    wordAlreadyExists: 'This word is already in your vocabulary.',
    addWordFailed: 'Could not add this word. Please try again.',
    addPhraseFailed: 'Could not add this phrase. Please try again.',
    deleteWord: 'Delete word',
    removeFromCollection: 'Remove from collection',
    sortAlphabetical: 'A–Z',
    sortDateAdded: 'Recent',
    sortAlphabeticalAria: 'Sort alphabetically',
    sortDateAddedAria: 'Sort by date added',
    unitFilterAll: 'All',
    unitFilterWords: 'Words',
    unitFilterPhrases: 'Phrases',
    unitFilterIdioms: 'Idioms',
    unitFilterPhrasal: 'Phrasal verbs',
    loadMore: 'Load more',
  },
  word: {
    allMeanings: 'All meanings',
    otherMeanings: 'Other meanings',
    learningThisSense: "You're learning",
    reviewHistory: 'Review history',
    reviewRating: {
      0: 'Again',
      1: 'Hard',
      2: 'Good',
      3: 'Easy',
    } as Record<number, string>,
    learnAnotherMeaning: 'Learn another meaning',
    setPrimarySense: 'Set primary',
    removeSense: 'Remove',
    removeSenseConfirm: 'Stop learning “{translation}”? The word stays in your vocabulary; this meaning can be added again later.',
    alsoLearning: 'Also learning',
    sensePickerTitle: 'Which meaning do you want to learn?',
    sensePickerHint: 'Choose one to save',
    inThisSentence: 'In this sentence',
    senseAlreadyLearned: 'Already learning this meaning',
    /** After saving a word from reading — bridges to review habit */
    saveWordMeetAgain: 'Saved — you’ll meet “{word}” in review',
    reEncounterBanner: 'From your reading',
    expression: 'Expression',
    literalMeaning: 'Literal',
    nestedWord: 'Word you tapped',
    nestedGrammarWord: 'Main verb',
    composedGrammarHero: 'In this sentence',
    saveGrammarEpisode: 'Save this moment',
    saveGrammarEpisodeSaved: 'Moment saved',
    /** C4 — {span} = surface from the article (e.g. “had been working”) */
    saveGrammarEpisodeMeetAgain: 'Saved — you’ll meet “{span}” in review',
    saveGrammarEpisodeFailed: 'Could not save this moment',
    saveGrammarEpisodeNeedMeaning: 'Need a meaning before saving this moment',
    saveNestedWord: 'Save this word',
    saveNestedWordSaved: 'Word saved',
    saveNestedWordMeetAgain: 'Saved — you’ll meet “{word}” in review',
    savePhrase: 'Save this phrase',
    savePhraseSaved: 'Already in vocabulary',
    savePhraseFailed: 'Could not save phrase',
    savePhraseTeaser: 'Save this phrase',
    savePhraseTeaserHint: 'Add this phrase to your vocabulary for review',
    chooserTitle: 'What did you want explained?',
    chooserPhrase: 'The expression',
    chooserPattern: 'The pattern',
    chooserWord: 'Just the word',
    masteredWarning:
      "This word hasn't been reviewed enough for long-term memory. Mark as mastered anyway?",
    aiGenerated: 'AI-generated',
    aiGeneratedHint: 'This word knowledge comes from AI and has not been fully validated yet.',
    reportIssue: 'Report a problem',
    reportIssueTitle: 'Something wrong here?',
    reportIssueHint: 'Wrong meaning, incomplete, or unclear — tell us in a sentence.',
    reportIssuePlaceholder: "What's wrong or missing?",
    reportIssueSubmit: 'Send report',
    reportIssueDone: 'Thanks — we’ll check this.',
    reportIssueError: 'Could not send. Try again in a moment.',
    reportIssueClose: 'Close report',
  },
  settings: {
    goalsNewWords: 'New words per day',
    goalsReviews: 'Reviews per day',
    goalsArticles: 'Articles per day',
    goalsPractice: 'AI practice per day',
    goalsPracticeProHint:
      'AI practice is included with Pro. Your goal is saved and will apply when you upgrade.',
    goalsPracticeOff: 'Off',
    preferredTopics: 'Topics you enjoy',
    preferredTopicsHint: 'Used to personalize reading recommendations',
    includeQuickChallenge: 'Include quick challenge in guided session',
    includeQuickChallengeHint: 'Adds a short vocabulary check to your daily session',
    notificationsComingSoon: 'Coming soon',
    notificationsComingSoonHint:
      'Weekly email digest is in development. We will enable this toggle when delivery is ready.',
    defaultReviewMode: 'Default review mode',
    defaultReviewModeHint: 'Used when you start a standalone refresh session',
    nativeLanguageChangeTitle: 'Switch native language?',
    nativeLanguageChangeBody: (languageLabel: string) =>
      `Your vocabulary is organized by language. After switching, you'll see the words you saved for ${languageLabel}, with translations in that language. If you haven't added words for ${languageLabel} yet, your list may look empty until you do. Words saved for other languages are kept — switch back anytime. AI explanations, word popups, and review cards will also use ${languageLabel}.`,
    nativeLanguageChangeConfirm: 'Switch language',
    nativeLanguageChangeCancel: 'Cancel',
  },
  landing: {
    heroTitle: 'Understand English as you read.',
    heroSubtitleLines: ['Tap any word.', "We'll explain it.", "You'll remember it."],
    demoHint: 'No account required',
    demoReadLabel: 'Read',
    demoUnderstandLabel: 'Understand',
    demoTapHint: 'Tap a highlighted word to see what it means',
    demoMeans: 'Means',
    demoInContext: 'In this sentence',
    demoLocalizedNote: 'Sign up to save words and meet them again in review.',
    signUpToSave: 'Sign up to save words',
    savedNudge: 'Saved — you’ll review “{word}” tomorrow.',
    capabilitiesTitle: 'One loop for real progress',
    capabilitiesSubtitle:
      'You don’t need thousands of flashcards. Read, save what matters, and come back when it counts.',
    finalTitle: 'Ready to keep what you understood?',
    getStarted: 'Get started free',
    signIn: 'Sign in',
  },
  faq: {
    title: 'Frequently asked questions',
    intro:
      'Short answers about how Lexio works today — reading, saving words, reviews, and your account.',
    feedbackTitle: 'Tell us what you think',
    feedbackHint: 'Likes, friction, bugs, ideas — we read every note.',
  },
  feedback: {
    like: 'I like this',
    dislike: 'This is confusing',
    bug: 'Something broke',
    idea: 'Idea',
    placeholder: 'What should we know?',
    emailPlaceholder: 'Your email',
    submit: 'Send feedback',
    done: 'Thanks — we got your feedback.',
    sendAnother: 'Send another',
    error: 'Could not send feedback. Try again in a moment.',
    settingsTitle: 'Feedback',
    settingsHint: 'Tell us what helps, what confuses you, or what broke.',
  },
} as const

export type Copy = typeof copy
